import { useEffect, useRef } from 'react';
import { WS2_AMENITY_NAMES, WS2_AMENITY_SLUGS } from '../../../../constants/amenities';

function labelFor(slug) {
    return WS2_AMENITY_NAMES[slug] ?? slug;
}

function FlatCheck() {
    return (
        <svg className="flat-check" width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <polyline points="2,7 6,11 12,3" />
        </svg>
    );
}

function resolveDismissed(listing, dirtyFields) {
    if (dirtyFields && 'amenitiesDismissed' in dirtyFields) {
        return dirtyFields.amenitiesDismissed ?? [];
    }
    return listing.enrichmentFields?.amenitiesDismissed ?? [];
}

function resolveConfirmed(listing, dirtyFields) {
    if (dirtyFields && 'amenities' in dirtyFields) {
        return dirtyFields.amenities ?? [];
    }
    const confirmed = listing.confirmedFields?.amenities;
    if (Array.isArray(confirmed)) return confirmed;
    return [];
}

function resolveAiSuggested(listing) {
    const enriched = listing.enrichmentFields?.amenities;
    if (!Array.isArray(enriched)) return [];
    return enriched.map((a) => a?.slug).filter(Boolean);
}

export default function BulkAmenitiesSection({
    listing,
    editMode = false,
    dirtyFields,
    setDirtyFields,
    onRequestEdit,
    onRequestEditWithSeed,
}) {
    const syncDirtyRef = useRef(null);

    useEffect(() => {
        syncDirtyRef.current = dirtyFields ?? null;
    }, [dirtyFields]);

    const safePatch = (mutator) => {
        const base = dirtyFields ?? syncDirtyRef.current;
        if (!base) {
            const seed = onRequestEditWithSeed?.();
            if (!seed) return;
            syncDirtyRef.current = seed;
            const next = mutator(seed);
            syncDirtyRef.current = next;
            setDirtyFields?.(next);
            return;
        }
        setDirtyFields?.((prev) => {
            const resolved = prev ?? syncDirtyRef.current;
            if (!resolved) return prev;
            const next = mutator(resolved);
            syncDirtyRef.current = next;
            return next;
        });
    };

    const confirmed = resolveConfirmed(listing, dirtyFields);
    const dismissed = resolveDismissed(listing, dirtyFields);
    const aiSuggested = resolveAiSuggested(listing);
    const pending = aiSuggested.filter((slug) => !confirmed.includes(slug) && !dismissed.includes(slug));
    const remaining = WS2_AMENITY_SLUGS.filter((slug) => !confirmed.includes(slug));

    const keepAmenity = (slug) => {
        safePatch((prev) => ({
            ...prev,
            amenities: [...(prev.amenities ?? []), slug].filter((s, i, arr) => arr.indexOf(s) === i),
        }));
    };

    const dismissAmenity = (slug) => {
        safePatch((prev) => ({
            ...prev,
            amenitiesDismissed: [...(prev.amenitiesDismissed ?? []), slug].filter((s, i, arr) => arr.indexOf(s) === i),
        }));
    };

    const removeAmenity = (slug) => {
        if (!editMode) return;
        safePatch((prev) => ({
            ...prev,
            amenities: (prev.amenities ?? []).filter((s) => s !== slug),
        }));
    };

    const addAmenity = (slug) => {
        if (!slug || !editMode) return;
        safePatch((prev) => ({
            ...prev,
            amenities: [...(prev.amenities ?? []), slug],
        }));
    };

    const confirmAllPending = () => {
        if (!pending.length) return;
        safePatch((prev) => ({
            ...prev,
            amenities: [
                ...(prev.amenities ?? []),
                ...pending,
            ].filter((s, i, arr) => arr.indexOf(s) === i),
        }));
    };

    const pendingCount = pending.length;
    const confirmedCount = confirmed.length;
    const headlineValue = pendingCount > 0
        ? `${pendingCount} AI-suggested amenit${pendingCount === 1 ? 'y' : 'ies'} pending`
        : 'All amenities confirmed';
    const subText = pendingCount > 0
        ? `${confirmedCount} confirmed · ${pendingCount} pending · AI-suggested from satellite, Places, and category benchmarks.`
        : `${confirmedCount} confirmed.`;
    const showEmptyEditLink = confirmedCount === 0 && pendingCount === 0 && !editMode && onRequestEdit;

    return (
        <div className="host-panel-section">
            <div className="host-panel-section-label">
                Amenities
                <span className="section-aux">
                    {confirmedCount} confirmed · {pendingCount} pending
                </span>
            </div>

            <div className="host-ai-section">
                <div className="host-ai-aggregate">
                    <div>
                        <div className="host-ai-aggregate-label">Status</div>
                        <div className="host-ai-aggregate-value">{headlineValue}</div>
                        <div className="host-ai-aggregate-sub">{subText}</div>
                    </div>
                    <div className="host-ai-aggregate-actions">
                        {pendingCount > 0 ? (
                            <button
                                type="button"
                                className="btn host-ai-field-confirm"
                                onClick={confirmAllPending}
                            >
                                <FlatCheck />
                                {' '}
                                Confirm all ({pendingCount})
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn host-ai-field-confirm"
                                disabled
                            >
                                All confirmed
                            </button>
                        )}
                    </div>
                </div>

                {pendingCount > 0 && (
                    <>
                        <div className="amenities-pending-label">AI suggested — tap ✓ to keep or × to dismiss</div>
                        <div className="host-amenities-chips amenities-pending">
                            {pending.map((slug) => (
                                <span key={slug} className="host-amenity-chip pending">
                                    {labelFor(slug)}
                                    <button
                                        type="button"
                                        className="amen-act dismiss"
                                        aria-label={`Dismiss ${labelFor(slug)}`}
                                        onClick={() => dismissAmenity(slug)}
                                    >
                                        ×
                                    </button>
                                    <button
                                        type="button"
                                        className="amen-act keep"
                                        aria-label={`Keep ${labelFor(slug)}`}
                                        onClick={() => keepAmenity(slug)}
                                    >
                                        <FlatCheck />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </>
                )}

                <div className="host-panel-section-header">
                    <div className="amenities-confirmed-label">Your amenities</div>
                    {confirmedCount > 0 && !editMode && onRequestEdit && (
                        <button
                            type="button"
                            className="host-section-edit-link"
                            onClick={onRequestEdit}
                        >
                            Edit →
                        </button>
                    )}
                </div>
                <div className="host-amenities-chips">
                    {confirmed.length === 0 ? (
                        <>
                            <span className="drawer-empty-amenities-note">No confirmed amenities yet.</span>
                            {showEmptyEditLink && (
                                <button
                                    type="button"
                                    className="host-section-edit-link"
                                    onClick={onRequestEdit}
                                >
                                    Edit →
                                </button>
                            )}
                        </>
                    ) : (
                        confirmed.map((slug) => (
                            <span key={slug} className="host-amenity-chip">
                                {labelFor(slug)}
                                {editMode && (
                                    <button
                                        type="button"
                                        className="x"
                                        aria-label={`Remove ${labelFor(slug)}`}
                                        onClick={() => removeAmenity(slug)}
                                    >
                                        ×
                                    </button>
                                )}
                            </span>
                        ))
                    )}
                    {editMode && remaining.length > 0 && (
                        <select
                            className="host-amenity-add"
                            value=""
                            onChange={(e) => addAmenity(e.target.value)}
                        >
                            <option value="">+ Add amenity</option>
                            {remaining.map((slug) => (
                                <option key={slug} value={slug}>{labelFor(slug)}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>
        </div>
    );
}
